import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  IconChevronDown,
  IconChevronRight,
  IconBuildingSkyscraper,
  IconMap2,
  IconMapPin,
  IconSubtask,
  IconHome,
  IconPlus,
} from "@tabler/icons-react";

import { api } from "../lib/api";
import type { Block, Company, Division, Estate, Region } from "../lib/types";
import { Modal } from "../components/Modal";
import { CompanyForm } from "../components/CompanyForm";
import { EstateForm } from "../components/EstateForm";
import { DivisionForm } from "../components/DivisionForm";
import { BlockForm } from "../components/BlockForm";
import { EstateTitlesModal } from "../components/EstateTitlesModal";
import { RowActionsMenu } from "../components/RowActionsMenu";
import { IconFileText } from "@tabler/icons-react";

type CreateModal =
  | { kind: "estate"; companyId: string }
  | { kind: "division"; estateId: string }
  | { kind: "block"; divisionId: string }
  | { kind: "editCompany"; company: Company }
  | { kind: "editEstate"; estate: Estate }
  | { kind: "editDivision"; division: Division }
  | { kind: "titles"; estate: Estate }
  | null;

export function EstateLandHierarchy() {
  const [modal, setModal] = useState<CreateModal>(null);

  const regionsQ = useQuery({
    queryKey: ["regions"],
    queryFn: () => api.list<Region>("/v1/regions"),
  });
  const companiesQ = useQuery({
    queryKey: ["companies"],
    queryFn: () => api.list<Company>("/v1/companies"),
  });
  const estatesQ = useQuery({
    queryKey: ["estates"],
    queryFn: () => api.list<Estate>("/v1/estates"),
  });
  const divisionsQ = useQuery({
    queryKey: ["divisions"],
    queryFn: () => api.list<Division>("/v1/divisions"),
  });
  const blocksQ = useQuery({
    queryKey: ["blocks"],
    queryFn: () => api.list<Block>("/v1/blocks?limit=500"),
  });

  const tree = useMemo(() => {
    const regions = regionsQ.data?.data ?? [];
    const companies = companiesQ.data?.data ?? [];
    const estates = estatesQ.data?.data ?? [];
    const divisions = divisionsQ.data?.data ?? [];
    const blocks = blocksQ.data?.data ?? [];

    return regions.map((r) => ({
      ...r,
      companies: companies
        .filter((c) => c.regionId === r.id)
        .map((c) => ({
          ...c,
          estates: estates
            .filter((e) => e.companyId === c.id)
            .map((e) => ({
              ...e,
              divisions: divisions
                .filter((d) => d.estateId === e.id)
                .map((d) => ({
                  ...d,
                  blocks: blocks.filter((b) => b.divisionId === d.id),
                })),
            })),
        })),
    }));
  }, [regionsQ.data, companiesQ.data, estatesQ.data, divisionsQ.data, blocksQ.data]);

  const isLoading =
    regionsQ.isLoading ||
    companiesQ.isLoading ||
    estatesQ.isLoading ||
    divisionsQ.isLoading ||
    blocksQ.isLoading;

  return (
    <div>
      <div className="mb-3 text-xs text-text-secondary">
        Region → Company → Estate → Division → Block. Click a block to drill in. Use the{" "}
        <IconPlus size={11} className="inline -mt-0.5" /> buttons to add children at each level.
      </div>

      {isLoading ? (
        <div className="p-6 text-center text-xs text-text-secondary">Loading hierarchy…</div>
      ) : tree.length === 0 ? (
        <div className="p-6 text-center text-xs text-text-secondary">
          No regions yet. Seed with the API server's <code>scripts/seed.ts</code>.
        </div>
      ) : (
        <div className="border border-border rounded overflow-hidden bg-surface-2">
          {tree.map((region) => (
            <RegionNode key={region.id} region={region} onAdd={setModal} />
          ))}
        </div>
      )}

      {/* Layer summary */}
      <div className="mt-4 grid grid-cols-5 gap-2 text-xs">
        <LayerSummary label="Regions" icon={<IconHome size={13} />} count={regionsQ.data?.data.length ?? 0} />
        <LayerSummary
          label="Companies"
          icon={<IconBuildingSkyscraper size={13} />}
          count={companiesQ.data?.data.length ?? 0}
        />
        <LayerSummary label="Estates" icon={<IconMap2 size={13} />} count={estatesQ.data?.data.length ?? 0} />
        <LayerSummary
          label="Divisions"
          icon={<IconMapPin size={13} />}
          count={divisionsQ.data?.data.length ?? 0}
        />
        <LayerSummary label="Blocks" icon={<IconSubtask size={13} />} count={blocksQ.data?.data.length ?? 0} />
      </div>

      {/* Create modals */}
      <Modal
        open={modal?.kind === "estate"}
        onClose={() => setModal(null)}
        title="New estate"
        subtitle="Belongs to a company. Set the title area to enable the land balance check."
        maxWidth="620px"
      >
        {modal?.kind === "estate" && (
          <EstateForm
            defaultCompanyId={modal.companyId}
            onSaved={() => setModal(null)}
            onCancel={() => setModal(null)}
          />
        )}
      </Modal>

      <Modal
        open={modal?.kind === "division"}
        onClose={() => setModal(null)}
        title="New division"
        subtitle="Sub-estate supervisory unit — each division has its own set of blocks."
        maxWidth="480px"
      >
        {modal?.kind === "division" && (
          <DivisionForm
            defaultEstateId={modal.estateId}
            onSaved={() => setModal(null)}
            onCancel={() => setModal(null)}
          />
        )}
      </Modal>

      <Modal
        open={modal?.kind === "block"}
        onClose={() => setModal(null)}
        title="New block"
        subtitle="Category-specific fields appear below. Cross-field rules validated server-side."
        maxWidth="600px"
      >
        {modal?.kind === "block" && (
          <BlockForm onSaved={() => setModal(null)} onCancel={() => setModal(null)} />
        )}
      </Modal>

      {/* Edit modals */}
      <Modal
        open={modal?.kind === "editCompany"}
        onClose={() => setModal(null)}
        title="Edit company"
        maxWidth="480px"
      >
        {modal?.kind === "editCompany" && (
          <CompanyForm
            initial={modal.company}
            onSaved={() => setModal(null)}
            onCancel={() => setModal(null)}
          />
        )}
      </Modal>

      <Modal
        open={modal?.kind === "editEstate"}
        onClose={() => setModal(null)}
        title="Edit estate"
        maxWidth="620px"
      >
        {modal?.kind === "editEstate" && (
          <EstateForm
            initial={modal.estate}
            onSaved={() => setModal(null)}
            onCancel={() => setModal(null)}
          />
        )}
      </Modal>

      <Modal
        open={modal?.kind === "editDivision"}
        onClose={() => setModal(null)}
        title="Edit division"
        maxWidth="480px"
      >
        {modal?.kind === "editDivision" && (
          <DivisionForm
            initial={modal.division}
            onSaved={() => setModal(null)}
            onCancel={() => setModal(null)}
          />
        )}
      </Modal>

      {/* Land titles manager — standalone (bigger than Modal supports) */}
      {modal?.kind === "titles" && (
        <EstateTitlesModal
          estate={modal.estate}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

interface RegionNodeData extends Region {
  companies: (Company & {
    estates: (Estate & { divisions: (Division & { blocks: Block[] })[] })[];
  })[];
}

function AddBtn({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className="text-text-accent hover:bg-bg-accent px-1.5 py-0.5 rounded text-[10.5px] flex items-center gap-0.5"
      title={label}
    >
      <IconPlus size={11} /> {label}
    </button>
  );
}

function RegionNode({
  region,
  onAdd,
}: {
  region: RegionNodeData;
  onAdd: (m: CreateModal) => void;
}) {
  const [open, setOpen] = useState(true);
  const totalCompanies = region.companies.length;
  return (
    <div>
      <TreeRow
        indent={0}
        icon={<IconHome size={14} className="text-text-secondary" />}
        chevron={
          <button
            onClick={() => setOpen(!open)}
            className="text-text-secondary hover:text-text-primary"
          >
            {open ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
          </button>
        }
        label={
          <>
            <span className="font-mono text-xs bg-surface-1 px-1.5 py-0.5 rounded">
              {region.code}
            </span>
            <span className="ml-2 font-medium">{region.name}</span>
          </>
        }
        meta={`${totalCompanies} ${totalCompanies === 1 ? "company" : "companies"}`}
      />
      {open && region.companies.map((c) => <CompanyNode key={c.id} company={c} onAdd={onAdd} />)}
    </div>
  );
}

function CompanyNode({
  company,
  onAdd,
}: {
  company: RegionNodeData["companies"][number];
  onAdd: (m: CreateModal) => void;
}) {
  const [open, setOpen] = useState(true);
  return (
    <div>
      <TreeRow
        indent={1}
        icon={<IconBuildingSkyscraper size={14} className="text-text-secondary" />}
        chevron={
          <button
            onClick={() => setOpen(!open)}
            className="text-text-secondary hover:text-text-primary"
          >
            {open ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
          </button>
        }
        label={
          <>
            <span className="font-mono text-xs bg-surface-1 px-1.5 py-0.5 rounded">
              {company.code}
            </span>
            <span className="ml-2">{company.name}</span>
          </>
        }
        meta={`${company.estates.length} ${company.estates.length === 1 ? "estate" : "estates"}`}
        action={
          <div className="flex items-center gap-1">
            <AddBtn label="Add estate" onClick={() => onAdd({ kind: "estate", companyId: company.id })} />
            <RowActionsMenu
              deletePath={`/v1/companies/${company.id}`}
              invalidateKeys={["companies", "estates", "divisions", "blocks"]}
              entityLabel={`company ${company.code}`}
              onEdit={() => onAdd({ kind: "editCompany", company })}
            />
          </div>
        }
      />
      {open && company.estates.map((e) => <EstateNode key={e.id} estate={e} onAdd={onAdd} />)}
    </div>
  );
}

function EstateNode({
  estate,
  onAdd,
}: {
  estate: RegionNodeData["companies"][number]["estates"][number];
  onAdd: (m: CreateModal) => void;
}) {
  const [open, setOpen] = useState(true);
  return (
    <div>
      <TreeRow
        indent={2}
        icon={<IconMap2 size={14} className="text-text-secondary" />}
        chevron={
          <button
            onClick={() => setOpen(!open)}
            className="text-text-secondary hover:text-text-primary"
          >
            {open ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
          </button>
        }
        label={
          <>
            <span className="font-mono text-xs bg-surface-1 px-1.5 py-0.5 rounded">
              {estate.code}
            </span>
            <span className="ml-2">{estate.name}</span>
            {estate.type && (
              <span className="ml-2 text-[10.5px] text-text-secondary uppercase">{estate.type}</span>
            )}
            {estate.titleAreaHa && (
              <span className="ml-2 text-[10.5px] text-text-secondary font-mono">
                title {Number(estate.titleAreaHa).toFixed(0)} ha
              </span>
            )}
          </>
        }
        meta={`${estate.divisions.length} ${estate.divisions.length === 1 ? "division" : "divisions"}`}
        action={
          <div className="flex items-center gap-1">
            <button
              onClick={() => onAdd({ kind: "titles", estate })}
              className="flex items-center gap-1 rounded bg-slate-800 px-2 py-1 text-xs text-white hover:bg-slate-900"
              title="Manage land titles for this estate"
            >
              <IconFileText size={12} />
              Titles
            </button>
            <AddBtn label="Add division" onClick={() => onAdd({ kind: "division", estateId: estate.id })} />
            <RowActionsMenu
              deletePath={`/v1/estates/${estate.id}`}
              invalidateKeys={["estates", "divisions", "blocks"]}
              entityLabel={`estate ${estate.code}`}
              onEdit={() => onAdd({ kind: "editEstate", estate })}
            />
          </div>}
      />
      {open && estate.divisions.map((d) => <DivisionNode key={d.id} division={d} onAdd={onAdd} />)}
    </div>
  );
}

function DivisionNode({
  division,
  onAdd,
}: {
  division: RegionNodeData["companies"][number]["estates"][number]["divisions"][number];
  onAdd: (m: CreateModal) => void;
}) {
  const [open, setOpen] = useState(true);
  return (
    <div>
      <TreeRow
        indent={3}
        icon={<IconMapPin size={14} className="text-text-secondary" />}
        chevron={
          <button
            onClick={() => setOpen(!open)}
            className="text-text-secondary hover:text-text-primary"
          >
            {open ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
          </button>
        }
        label={
          <>
            <span className="font-mono text-xs bg-surface-1 px-1.5 py-0.5 rounded">
              {division.code}
            </span>
            <span className="ml-2">{division.name}</span>
          </>
        }
        meta={`${division.blocks.length} ${division.blocks.length === 1 ? "block" : "blocks"}`}
        action={
          <div className="flex items-center gap-1">
            <AddBtn label="Add block" onClick={() => onAdd({ kind: "block", divisionId: division.id })} />
            <RowActionsMenu
              deletePath={`/v1/divisions/${division.id}`}
              invalidateKeys={["divisions", "blocks"]}
              entityLabel={`division ${division.code}`}
              onEdit={() => onAdd({ kind: "editDivision", division })}
            />
          </div>
        }
      />
      {open &&
        division.blocks.map((b) => (
          <Link key={b.id} to={`/estate-land/blocks/${b.id}`} className="block hover:bg-surface-1">
            <TreeRow
              indent={4}
              icon={<IconSubtask size={12} className="text-text-muted" />}
              label={
                <>
                  <span className="font-mono text-xs">
                    <span className="text-text-secondary">{division.code}</span>
                    <span className="text-text-muted mx-1">·</span>
                    {b.code}
                  </span>
                  <span className="ml-2 text-[10.5px]">
                    <CategoryTiny cat={b.category} />
                  </span>
                  {b.hectaresTotal && (
                    <span className="ml-2 text-[10.5px] text-text-secondary font-mono">
                      {Number(b.hectaresTotal).toFixed(2)} ha
                    </span>
                  )}
                  {b.innerRoadToMill && (
                    <span className="ml-2 text-[10.5px] bg-bg-warning text-text-warning px-1.5 py-0.5 rounded font-mono">
                      inner_rd {b.millRoadKm ? `· ${b.millRoadKm}km` : ""}
                    </span>
                  )}
                </>
              }
              meta="→"
            />
          </Link>
        ))}
    </div>
  );
}

function TreeRow({
  indent,
  chevron,
  icon,
  label,
  meta,
  action,
}: {
  indent: number;
  chevron?: React.ReactNode;
  icon: React.ReactNode;
  label: React.ReactNode;
  meta?: string;
  action?: React.ReactNode;
}) {
  return (
    <div
      className="flex items-center py-1.5 px-3 border-b border-border last:border-b-0 text-xs group"
      style={{ paddingLeft: `${12 + indent * 20}px` }}
    >
      <div className="w-4 mr-1">{chevron}</div>
      <div className="mr-2">{icon}</div>
      <div className="flex-1">{label}</div>
      {action && (
        <div className="opacity-0 group-hover:opacity-100 transition-opacity mr-2">
          {action}
        </div>
      )}
      {meta && <div className="text-text-secondary text-[10.5px]">{meta}</div>}
    </div>
  );
}

function CategoryTiny({ cat }: { cat: Block["category"] }) {
  const cls =
    cat === "PLANTED"
      ? "bg-bg-success text-text-success"
      : cat === "LAND_CLEARING"
        ? "bg-bg-warning text-text-warning"
        : "bg-surface-1 text-text-muted border border-border";
  return (
    <span className={`px-1.5 py-0.5 rounded text-[9.5px] ${cls}`}>
      {cat === "LAND_CLEARING" ? "Land clearing" : cat === "PLANTED" ? "Planted" : "Unplanted"}
    </span>
  );
}

function LayerSummary({
  label,
  icon,
  count,
}: {
  label: string;
  icon: React.ReactNode;
  count: number;
}) {
  return (
    <div className="bg-surface-1 rounded p-2 flex items-center gap-2">
      <div className="text-text-secondary">{icon}</div>
      <div>
        <div className="text-[10px] text-text-secondary">{label}</div>
        <div className="font-medium">{count}</div>
      </div>
    </div>
  );
}
